import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

// UserDetail component for displaying and editing user information
const UserDetail = () => {
    // State and route management
    const { userID } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState(null);
    const [heartAdjustment, setHeartAdjustment] = useState("");
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [updateHeartsLoading, setUpdateHeartsLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [userPrivilege, setUserPrivilege] = useState("");

    // Fetch user data from API
    const fetchUserData = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate("/");
                return;
            }

            const response = await fetch('/api/Customers', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error('Failed to fetch user data');

            const data = await response.json();
            const userData = data.find(u => u.userid === parseInt(userID));
            if (!userData) throw new Error('User not found');

            setUser(userData);
            setFormData({
                firstname: userData.firstname,
                lastname: userData.lastname,
                email: userData.email,
                city: userData.city,
                note: userData.note,
                familymembers: userData.familymembers,
                adjustHearts: 0,
                swoppacketid: userData.swoppacketid || 0,
                monthlyclotheshandedin: userData.monthlyclotheshandedin || 0,
                isMember: userData.isMember
            });
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    }, [userID, navigate]);

    // Fetch user logs from API
    const fetchUserLogs = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/logs/${user.email}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch logs');
            }

            const data = await response.json();
            setLogs(data);
        } catch (err) {
            setError(err.message);
        }
    }, [user]);

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    useEffect(() => {
        if (user) {
            fetchUserLogs();
        }
    }, [user, fetchUserLogs]);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUserPrivilege(decoded.privilege);
            } catch (error) {
                console.error("Error decoding token:", error);
            }
        }
    }, []);

    // Handle user deletion
    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this customer? This action cannot be undone.")) {
            return;
        }

        setDeleteLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/');
                return;
            }

            const response = await fetch(`/api/Customer/${userID}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(errorData || 'Failed to delete customer');
            }

            navigate('/dashboard');
        } catch (err) {
            setError(err.message);
            setDeleteLoading(false);
        }
    };

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Handle hearts adjustment
    const handleHeartUpdate = async () => {
        if (!heartAdjustment.startsWith('+') && !heartAdjustment.startsWith('-')) {
            setError('Please enter a value starting with + or -');
            return;
        }

        const numericAdjustment = parseInt(heartAdjustment, 10);
        if (isNaN(numericAdjustment)) {
            setError('Please enter a valid number');
            return;
        }

        setUpdateHeartsLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/Customer', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...user,
                    adjustHearts: numericAdjustment
                })
            });

            if (!response.ok) throw new Error('Failed to update hearts');

            await fetchUserData();
            setHeartAdjustment("");
        } catch (err) {
            setError(err.message);
        } finally {
            setUpdateHeartsLoading(false);
        }
    };

    // Handle form submission for user updates
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/Customer', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...formData,
                    userid: parseInt(userID)
                })
            });

            if (!response.ok) throw new Error('Failed to update user');

            await fetchUserData();
            setIsEditing(false);
            setHeartAdjustment("");
        } catch (err) {
            setError(err.message);
        }
    };

    // Loading and error states
    if (loading) return <div className="p-8 text-center">Loading...</div>;
    if (error) return <div className="p-8 text-center text-red-500">Error: {error}</div>;
    if (!user) return <div className="p-8 text-center">User not found</div>;

    return (
        <div className="min-h-screen py-8" style={{ background: 'linear-gradient(to bottom, #b3e672 10%, #7ba02d 90%)' }}>
            <div className="max-w-4xl mx-auto px-4">
                {isEditing ? (
                    <div className="bg-white rounded-lg shadow-lg p-8">
                        <h2 className="text-2xl font-bold mb-6">Edit User Details</h2>
                        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                                <input
                                    type="text"
                                    name="firstname"
                                    value={formData.firstname}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
    
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                                <input
                                    type="text"
                                    name="lastname"
                                    value={formData.lastname}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
    
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
    
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
    
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Family Members</label>
                                <input
                                    type="text"
                                    name="familymembers"
                                    value={formData.familymembers}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
    
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
                                <textarea
                                    name="note"
                                    value={formData.note}
                                    onChange={handleChange}
                                    rows="3"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
    
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Swop Packet ID</label>
                                <input
                                    type="number"
                                    name="swoppacketid"
                                    value={formData.swoppacketid || 0}
                                    onChange={handleChange}
                                    min="0"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Clothes Handed In</label>
                                <input
                                    type="number"
                                    name="monthlyclotheshandedin"
                                    value={formData.monthlyclotheshandedin || 0}
                                    onChange={handleChange}
                                    min="0"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
    
                            <div className="col-span-2">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="isMember"
                                        id="isMember"
                                        checked={formData.isMember}
                                        onChange={(e) => handleChange({
                                            target: {
                                                name: 'isMember',
                                                value: e.target.checked
                                            }
                                        })}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="isMember" className="ml-2 block text-sm text-gray-900">
                                        Member
                                    </label>
                                </div>
                            </div>
    
                            <div className="col-span-2 flex justify-end space-x-4 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setIsEditing(false)}
                                    className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow-lg p-8">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">User Details</h2>
                            <div className="flex space-x-4">
                                <button
                                    onClick={() => navigate("/dashboard")}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                                >
                                    ← Back to Dashboard
                                </button>
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                                >
                                    Edit Details
                                </button>
                                {userPrivilege === "admin" && (
                                    <button
                                        onClick={handleDelete}
                                        disabled={deleteLoading}
                                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                                    >
                                        {deleteLoading ? "Deleting..." : "Delete User"}
                                    </button>
                                )}
                            </div>
                        </div>
    
                        <div className="grid grid-cols-2 gap-6 mb-8">
                            <div className="bg-gray-50 p-6 rounded-lg">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Name</p>
                                        <p className="mt-1 text-sm text-gray-900">{`${user.firstname} ${user.lastname}`}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Email</p>
                                        <p className="mt-1 text-sm text-gray-900">{user.email}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">City</p>
                                        <p className="mt-1 text-sm text-gray-900">{user.city}</p>
                                    </div>
                                </div>
                            </div>
    
                            <div className="bg-gray-50 p-6 rounded-lg">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Details</h3>
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Family Members</p>
                                        <p className="mt-1 text-sm text-gray-900">{user.familymembers || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Swop Packet ID</p>
                                        <p className="mt-1 text-sm text-gray-900">{user.swoppacketid || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Membership Status</p>
                                        <p className="mt-1 text-sm text-gray-900">{user.isMember ? 'Member' : 'Not a Member'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Hearts</p>
                                        <div className="mt-1 flex items-center space-x-4">
                                            <span className="text-lg font-bold text-gray-900">{user.hearts}</span>
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="text"
                                                    value={heartAdjustment}
                                                    onChange={(e) => setHeartAdjustment(e.target.value)}
                                                    placeholder="+/- hearts"
                                                    className="w-24 px-2 py-1 text-sm border rounded"
                                                />
                                                <button
                                                    onClick={handleHeartUpdate}
                                                    disabled={!heartAdjustment || updateHeartsLoading}
                                                    className="px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                                                >
                                                    {updateHeartsLoading ? "Updating..." : "Update"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
    
                        <div className="bg-gray-50 p-6 rounded-lg mb-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Notes</h3>
                            <p className="text-sm text-gray-900 whitespace-pre-wrap">{user.note || '-'}</p>
                        </div>
    
                        <div className="bg-gray-50 p-6 rounded-lg mb-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">User Logs</h3>
                            <div className="bg-gray-50 p-6 rounded-lg">
                                {logs.length > 0 ? (
                                    <div className="h-60 overflow-y-auto bg-white shadow rounded-lg p-4">
                                        <ul className="bg-white shadow rounded-lg p-4">
                                            {logs.slice().reverse().map((log, index) => (
                                                <li key={index} className="border-b last:border-b-0 py-2">
                                                    <p className="text-sm text-gray-600">{log.message}</p>
                                                    <p className="text-xs text-gray-400">{new Date(new Date(log.created_at).getTime() + 3600000).toLocaleString()}</p>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <div className="bg-white shadow rounded-lg p-4">
                                        <p className="text-gray-500">No logs available for this user.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserDetail;